// filepath: resources/js/components/pages/CMSPages/FriVia/Quizzes/QuestionAnswerCMS.jsx
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppBarCMS } from '../../../../basic/index.jsx';
import { CollapseBlock, Spinner } from '../../../../common/index.jsx';
import { useGetQuizQuestionQuery } from '../../../../../redux/services/quiz_questions.jsx';
import {
  useGetStudentAnswersQuery,
  useSaveStudentAnswerMutation,
} from '../../../../../redux/services/quiz_student_answers.jsx';
import { Radio, Space, Alert } from 'antd';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import { useDispatch } from 'react-redux';
import { showErrorPage } from '../../../../../redux/slices/coreSlice.jsx';
import { useGetLatestQuizRunQuery } from '../../../../../redux/services/quiz_runs.jsx';
import FormButtonsContainer from '../../../../antd/form/FormButtonsContainer/FormButtonsContainer.jsx';
import FormButton from '../../../../antd/form/FormButton/FormButton.jsx';

const QuestionAnswerCMS = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { id } = useParams();

  const pageTitle = t('Odpoveď na kvízovú otázku');

  useEffect(() => {
    document.title = pageTitle + ' - FRI CMS';
  }, [t]);

  const [runPollingInterval, setRunPollingInterval] = useState(5000);

  // Poll latest run by question id every 5s, faster once a run exists but is not running
  const { data: latestRun } = useGetLatestQuizRunQuery(
    { fv_question_id: Number(id) },
    { pollingInterval: runPollingInterval },
  );

  const hasRun = !!latestRun?.id;
  const isRunning = !!latestRun?.is_running;
  const showInactiveAlert = hasRun && !isRunning;

  useEffect(() => {
    setRunPollingInterval(hasRun && !isRunning ? 2000 : 5000);
  }, [hasRun, isRunning]);

  // State to forget question & answer after run ends
  const [clearedAfterEnd, setClearedAfterEnd] = useState(false);

  // Load question only while a run is actively running
  const {
    data: question,
    isLoading,
    isError,
    refetch,
  } = useGetQuizQuestionQuery(Number(id), {
    skip: !isRunning || clearedAfterEnd,
  });

  // Remove correctness logic – no correctness after run end
  const [selectedAnswer, setSelectedAnswer] = useState(undefined); // track selected Radio value
  const [submitted, setSubmitted] = useState(false);
  // correctness fetching disabled intentionally; no color coding based on correctness

  // Fetch current user's answers only when run is actively running
  const { data: myAnswers } = useGetStudentAnswersQuery(
    { fv_question_id: Number(id) },
    { skip: !isRunning },
  );
  const hasAnswered =
    isRunning && Array.isArray(myAnswers) && myAnswers.length > 0;

  // Refetch question immediately when run becomes actively running (transition false -> true)
  const prevIsRunningRef = useRef(isRunning);
  useEffect(() => {
    // Run transitioned from not running to running: allow fetching again
    if (prevIsRunningRef.current === false && isRunning) {
      if (clearedAfterEnd) setClearedAfterEnd(false); // enable fresh fetch
    }

    // Run transitioned from running to not running: forget question & answer
    if (prevIsRunningRef.current === true && !isRunning) {
      setSelectedAnswer(undefined); // also clear which answer (radio) was chosen
      setSubmitted(false);
      setValidationError(null);
      setClearedAfterEnd(true); // prevents showing stale question until next run
    }

    // When we have no running question yet, reset stale question state once
    if (!isRunning) {
      setSelectedAnswer(undefined);
      setSubmitted(false);
      if (hasRun) {
        setClearedAfterEnd(true);
      }
    }

    prevIsRunningRef.current = isRunning;
  }, [isRunning, refetch, clearedAfterEnd, hasRun]);

  // Ensure global error overlay is not shown for this page
  useEffect(() => {
    dispatch(showErrorPage(undefined));
  }, [dispatch]);
  useEffect(() => {
    if (isError) {
      dispatch(showErrorPage(undefined));
    }
  }, [isError, dispatch]);

  const [saveAnswer, { isLoading: isSaving }] = useSaveStudentAnswerMutation();
  const { successMessage, saveFailed } = useToastMessages();

  // New state for suppressing 422 toast and showing inline alert instead
  const [validationError, setValidationError] = useState(null);

  // If the user already answered, previously we preselected; requirement changed: always clear radios when question shows
  useEffect(() => {
    if (question && isRunning && !clearedAfterEnd) {
      // Always clear selection when (re)displaying the question
      setSelectedAnswer(undefined);
      if (hasAnswered) {
        // Lock form (user already answered) but keep radios unselected
        setSubmitted(true);
      } else {
        setSubmitted(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question?.id, isRunning, clearedAfterEnd, hasAnswered]);

  // Reset validation error when question or run/selection changes
  useEffect(() => {
    setValidationError(null);
  }, [id, selectedAnswer, isRunning]);

  const answers = useMemo(() => {
    const list = Array.isArray(question?.answers) ? question.answers : [];
    return [...list].sort((a, b) => (a.position || 0) - (b.position || 0));
  }, [question]);

  const onSubmit = async () => {
    if (!question?.id || !question?.fv_quiz_id || !selectedAnswer) return;
    try {
      await saveAnswer({
        fv_quiz_id: Number(question.fv_quiz_id),
        fv_question_id: Number(question.id),
        fv_answer_id: Number(selectedAnswer),
      }).unwrap();
      successMessage({ message: t('Odpoveď odoslaná') });
      setSubmitted(true);
      setValidationError(null);
    } catch (err) {
      // Suppress global toast for 422 and show inline alert instead
      const status = err?.status ?? err?.originalStatus ?? err?.data?.status;
      if (status === 422) {
        const backendMsg =
          err?.data?.message ||
          err?.data?.errors?.[0] ||
          t('Odpoveď nebola odoslaná. Skúste znova.');
        setValidationError(backendMsg);
        return;
      }
      saveFailed(err);
    }
  };

  // While the question or refresh is loading, show spinner
  if (isLoading) {
    return <Spinner />;
  }

  if (showInactiveAlert || (!hasRun && !isRunning)) {
    return (
      <>
        <AppBarCMS title={pageTitle} permissionsBasic="quiz-student-answers" />
        <div style={{ margin: 10 }}>
          <Alert
            title={t('Kvízová otázka nie je k dispozícii')}
            description={t('Čas na odpoveď ešte nezačal alebo už vypršal.')}
            showIcon
            type="warning"
            style={{ marginBottom: 24 }}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <AppBarCMS title={pageTitle} permissionsBasic="quiz-student-answers" />
      <CollapseBlock title={t('Kvízová otázka')}>
        <div style={{ margin: 10 }}>
          {hasAnswered && isRunning && (
            <Alert
              type="info"
              showIcon
              title={t('Nie je možné odpovedať')}
              description={t('Na túto otázku ste už odpovedali')}
              style={{ marginBottom: 16 }}
            />
          )}
          {validationError && (
            <Alert
              type="error"
              showIcon
              title={t('Chyba pri odosielaní odpovede')}
              description={validationError}
              style={{ marginBottom: 16 }}
            />
          )}
          {!(hasAnswered && isRunning) && question && !clearedAfterEnd && (
            <>
              <div style={{ fontWeight: 600, marginBottom: 12 }}>
                {question.text}
              </div>
              <Radio.Group
                onChange={(e) => setSelectedAnswer(e.target.value)}
                value={selectedAnswer}
                disabled={submitted || hasAnswered}
              >
                <Space orientation="vertical" size={8}>
                  {answers.map((a) => {
                    const isChosen = Number(selectedAnswer) === Number(a.id);
                    const colorStyle =
                      isChosen && submitted ? { fontWeight: 600 } : {};
                    return (
                      <div
                        key={a.id}
                        style={{ display: 'flex', alignItems: 'center' }}
                      >
                        <Radio value={a.id} disabled={submitted || hasAnswered}>
                          <span style={{ ...colorStyle }}>{a.text}</span>
                        </Radio>
                      </div>
                    );
                  })}
                </Space>
              </Radio.Group>
              {isRunning && !clearedAfterEnd && (
                <div style={{ marginTop: 12 }}>
                  <FormButtonsContainer>
                    <FormButton
                      onClick={onSubmit}
                      loading={isSaving}
                      disabled={!selectedAnswer || submitted || hasAnswered}
                    >
                      {t('Odoslať odpoveď')}
                    </FormButton>
                  </FormButtonsContainer>
                </div>
              )}
            </>
          )}
        </div>
      </CollapseBlock>
    </>
  );
};

export default QuestionAnswerCMS;
